import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useCrmChat from '../../../hooks/useCrmChat';
import { isMobileContainer } from '../../../utils/device';
import FloatingCrmChatButton from './FloatingCrmChatButton';
import CrmChatPanel from './CrmChatPanel';

export default function CrmChatWidget({ hasFeedbackButton = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(false);
  const deepLinkHandledRef = useRef(false);

  const chatUserIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('crm_chat_user');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [location.search]);

  const {
    employees,
    loadingEmployees,
    messages,
    loadingMessages,
    loadingOlder,
    hasMoreMessages,
    unreadTotal,
    activeContact,
    searchQuery,
    setSearchQuery,
    replyTo,
    uploadProgress,
    highlightedMessageId,
    listContainerRef,
    openConversationWith,
    sendMessage,
    sendAttachment,
    loadOlderMessages,
    closeConversation,
    cancelReply,
    setReplyTarget,
    scrollToMessage,
    registerMessageRef,
    pendingNewCount,
    setNearBottom,
    clearPendingNew,
    consumeJustSent,
  } = useCrmChat({
    enabled: true,
    panelOpen: panelOpen || !!chatUserIdFromUrl,
    activeUserId: chatUserIdFromUrl,
  });

  useEffect(() => {
    if (!chatUserIdFromUrl) {
      deepLinkHandledRef.current = false;
      return;
    }
    setPanelOpen(true);
  }, [chatUserIdFromUrl]);

  useEffect(() => {
    if (!chatUserIdFromUrl || deepLinkHandledRef.current) return;
    if (!activeContact?.conversation_id) return;
    if (Number(activeContact.user_id) !== chatUserIdFromUrl) return;

    deepLinkHandledRef.current = true;
    const params = new URLSearchParams(location.search);
    params.delete('crm_chat_user');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [chatUserIdFromUrl, activeContact, location.pathname, location.search, navigate]);

  const handleToggle = () => {
    setPanelOpen((prev) => !prev);
  };

  const handleSelectEmployee = async (employee) => {
    await openConversationWith(employee);
  };

  const handleBack = () => {
    closeConversation();
  };

  const isMobile = isMobileContainer();
  const showFab = !(isMobile && panelOpen);

  return (
    <>
      {showFab && (
        <FloatingCrmChatButton
          onClick={handleToggle}
          unreadTotal={panelOpen ? 0 : unreadTotal}
          hasFeedbackButton={hasFeedbackButton}
        />
      )}
      <CrmChatPanel
        open={panelOpen}
        onClose={() => {
          setPanelOpen(false);
          closeConversation();
        }}
        employees={employees}
        loadingEmployees={loadingEmployees}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeContact={activeContact}
        onSelectEmployee={handleSelectEmployee}
        onBack={handleBack}
        messages={messages}
        loadingMessages={loadingMessages}
        loadingOlder={loadingOlder}
        hasMoreMessages={hasMoreMessages}
        onSend={sendMessage}
        onSendAttachment={sendAttachment}
        onLoadOlder={loadOlderMessages}
        replyTo={replyTo}
        onCancelReply={cancelReply}
        onReply={setReplyTarget}
        onScrollToReply={scrollToMessage}
        uploadProgress={uploadProgress}
        highlightedMessageId={highlightedMessageId}
        registerMessageRef={registerMessageRef}
        listContainerRef={listContainerRef}
        pendingNewCount={pendingNewCount}
        onNearBottomChange={setNearBottom}
        onClearPendingNew={clearPendingNew}
        consumeJustSent={consumeJustSent}
      />
    </>
  );
}
